import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Box, Stack, Typography } from "@mui/material";
import {
  hasNumber,
  hasUpperAndLowerCase,
  passwordMatch,
  validLength,
} from "@utils/functions/inputValidators";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  password: string;
  confirmPassword: string;
};

type ValidationItemProps = {
  isValid: boolean;
  label: string;
};

const ValidationItem: FunctionComponent<ValidationItemProps> = ({ isValid, label }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {isValid ? (
      <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
    )}
    <Typography
      variant="body2"
      sx={{
        color: isValid ? "success.main" : "text.secondary",
        fontWeight: isValid ? 500 : 400,
        transition: "all 0.2s ease-in-out",
      }}
    >
      {label}
    </Typography>
  </Stack>
);

export const PasswordValidator: FunctionComponent<Props> = ({ password, confirmPassword }) => {
  const { t } = useTranslation();

  const validations = [
    {
      isValid: validLength(password, 8),
      label: `${t("common.form.helper.password-must-contain")} 8 ${t("common.shared.characters")}`,
    },
    {
      isValid: hasNumber(password),
      label: `1 ${t("common.shared.number")}`,
    },
    {
      isValid: hasUpperAndLowerCase(password),
      label: `1 ${t("common.shared.uppercase")} ${t("common.shared.lowercase")}`,
    },
    {
      isValid: passwordMatch(password, confirmPassword),
      label: t("common.form.helper.passwords-match"),
    },
  ];

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: "grey.50",
        border: 1,
        borderColor: "grey.200",
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: "text.primary", fontWeight: 600 }}>
        {t("common.form.helper.password-must-contain")}
      </Typography>
      <Stack spacing={1}>
        {validations.map((validation) => (
          <ValidationItem
            key={validation.label}
            isValid={validation.isValid}
            label={validation.label}
          />
        ))}
      </Stack>
    </Box>
  );
};
